import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Box } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DOTS } from 'modules/admin-panel/pages/results/utils';

import { useStyles } from 'components/base/pagination/pagination.styles';

interface PageRangeProps {
	wrapperClass?: string;
	currentPage: number;
	paginationRange: Array<number | string>;
	onPageChange: (page: number) => void;
}

const firstPage = 1;

export const PageRange = ({
	wrapperClass,
	currentPage,
	paginationRange,
	onPageChange,
}: PageRangeProps) => {
	const classes = useStyles();
	const { t } = useTranslation('results');

	const lastPage = useMemo(() => paginationRange[paginationRange.length - 1], [
		paginationRange,
	]);

	const onNext = useCallback(() => {
		onPageChange(currentPage + 1);
	}, [onPageChange, currentPage]);

	const onPrevious = useCallback(() => {
		onPageChange(currentPage - 1);
	}, [onPageChange, currentPage]);

	return (
		<Box className={cn(classes.pagination, wrapperClass)}>
			<button
				type="button"
				className={cn(classes.pageItem, {
					disabled: currentPage === firstPage,
				})}
				onClick={onPrevious}
			>
				<ArrowBackIosIcon
					fontSize="small"
					color={currentPage === firstPage ? 'disabled' : 'primary'}
				/>
				{t('titles.pagination_prev')}
			</button>
			{paginationRange.map((pageNumber, index) => {
				if (pageNumber === DOTS) {
					return (
						<span
							// eslint-disable-next-line react/no-array-index-key
							key={`${pageNumber}-${index}`}
							className={cn(classes.pageItem, 'dots')}
						>
							&#8230;
						</span>
					);
				}

				return (
					<span
						className={cn(classes.pageItem, {
							selected: pageNumber === currentPage,
						})}
						key={pageNumber}
						onClick={() => onPageChange(pageNumber as number)}
					>
						{pageNumber}
					</span>
				);
			})}
			<button
				type="button"
				className={cn(classes.pageItem, {
					disabled: currentPage === lastPage,
				})}
				onClick={onNext}
			>
				{t('titles.pagination_next')}
				<ArrowForwardIosIcon
					fontSize="small"
					color={currentPage === lastPage ? 'disabled' : 'primary'}
				/>
			</button>
		</Box>
	);
};
